import { Component, EventEmitter, Output, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { QuoteFlowService } from '@services/quote-flow.service';
import { WorkService, WorkTypeDto, WorkItemDto } from '@services/work.service';
import { CalculatorStateService } from '@services/calculator-state.service';
import { AuthService } from '@services/auth.service';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import { ButtonSquareComponent } from '@atoms/button-square/button-square.component';
import { ButtonComponent } from '@atoms/button/button.component';
import { SvgIcon } from '@services/svg-icon.service';
import { NAVIGATION_ROUTES } from '@shared/constants/app-routes';
import { AnalyticsService } from '@services/analytics.service';

export interface ConstructionType {
  id: string;
  name: string;
  iconUrl: string;
  selected?: boolean;
}

export interface WorkTypeModalConfig {
  title?: string;
  subtitle?: string;
  customTypes?: ConstructionType[];
  targetRoute?: string;
  mode?: 'quote' | 'calculator';
}

@Component({
  selector: 'app-work-type-selection-modal',
  standalone: true,
  imports: [NgClass, SvgIconComponent, ButtonSquareComponent, ButtonComponent],
  templateUrl: './work-type-selection-modal.component.html'
})
export class WorkTypeSelectionModalComponent implements OnInit {
  @Output() confirmed = new EventEmitter<ConstructionType[]>();

  private router = inject(Router);
  private quoteFlowService = inject(QuoteFlowService);
  private workService = inject(WorkService);
  private calculatorStateService = inject(CalculatorStateService);
  private authService = inject(AuthService);
  private bottomSheetRef = inject(MatBottomSheetRef<WorkTypeSelectionModalComponent>);
  private cdr = inject(ChangeDetectorRef);
  private data: { preselectedTypes?: ConstructionType[]; config?: WorkTypeModalConfig } | null = inject(MAT_BOTTOM_SHEET_DATA, { optional: true });
  private analytics = inject(AnalyticsService);
  readonly SvgIcon = SvgIcon;

  selectedTypes: ConstructionType[] = [];
  constructionTypes: ConstructionType[] = [];
  loading = false;
  loadingItems = false;
  error: string | null = null;
  itemsError: string | null = null;

  ngOnInit() {
   this.loadWorkTypes();
  }

  /**
   * Carga los tipos de trabajo desde la API
   */
  loadWorkTypes(): void {
    this.loading = true;
    this.error = null;
    this.cdr.detectChanges(); // Forzar detección después del cambio inicial

    // Usar el servicio apropiado según el modo
    const workTypesObservable = this.data?.config?.mode === 'calculator' 
      ? this.workService.getCalculatorWorkTypes()
      : this.workService.getWorkTypes();

    workTypesObservable.subscribe({
      next: (workTypes: WorkTypeDto[]) => {
        this.constructionTypes = this.mapWorkTypesToConstructionTypes(workTypes);
        this.applyPreselectedTypes();
        this.loading = false;
        this.cdr.detectChanges(); // Forzar detección después de cargar
      },
      error: (error) => {
        console.error('Error loading work types:', error);
        this.error = 'Error al cargar los tipos de trabajo';
        this.loading = false;
        this.cdr.detectChanges(); // Forzar detección después de error
        this.loadFallbackData();
      }
    });
  }

  /**
   * Mapea la respuesta de la API al formato local
   */
  private mapWorkTypesToConstructionTypes(workTypes: WorkTypeDto[]): ConstructionType[] {
    let filteredWorkTypes = workTypes;
    
    return filteredWorkTypes
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(workType => ({
        id: workType.workTypeId,
        name: workType.description,
        iconUrl: workType.imgUrl,
        selected: false
      }));
  }

  /**
   * Datos de fallback en caso de error de API
   */
  private loadFallbackData(): void {
    this.constructionTypes = [
      {
        id: 'cimentacion',
        name: 'Cimentación',
        iconUrl: 'assets/icons/construction/cimentacion.svg',
        selected: false
      },
      {
        id: 'zapata',
        name: 'Zapata',
        iconUrl: 'assets/icons/construction/zapata.svg',
        selected: false
      },
      {
        id: 'muro',
        name: 'Muro',
        iconUrl: 'assets/icons/construction/muro.svg',
        selected: false
      },
      {
        id: 'columna',
        name: 'Columna',
        iconUrl: 'assets/icons/construction/columna.svg',
        selected: false
      },
      {
        id: 'losas-vigas',
        name: 'Losas y Vigas',
        iconUrl: 'assets/icons/construction/losas-vigas.svg',
        selected: false
      },
      {
        id: 'otro',
        name: 'Otro',
        iconUrl: 'assets/icons/construction/otro.svg',
        selected: false
      }
    ];
    this.applyPreselectedTypes();
  }

  /**
   * Aplica los tipos preseleccionados si were provideídos
   * En modo calculator, solo cuando se viene desde cambio de workType (step-2)
   */
  private applyPreselectedTypes(): void {
    // Solo aplicar preselección si se proporcionaron tipos preseleccionados
    if (this.data?.preselectedTypes?.length) {
      const preselectedIds = this.data.preselectedTypes.map(type => type.id);
      
      // En modo calculator, aplicar solo el primero (selección única)
      if (this.data?.config?.mode === 'calculator') {
        // Encontrar el primer tipo que coincida
        const firstMatchingType = this.constructionTypes.find(type => 
          preselectedIds.includes(type.id)
        );
        
        if (firstMatchingType) {
          // Deseleccionar todos primero
          this.constructionTypes.forEach(type => type.selected = false);
          // Seleccionar solo el que coincide
          firstMatchingType.selected = true;
          this.selectedTypes = [firstMatchingType];
        }
      } else {
        // Modo quote: permitir selección múltiple
        this.constructionTypes.forEach(type => {
          if (preselectedIds.includes(type.id)) {
            type.selected = true;
          }
        });
        this.selectedTypes = this.constructionTypes.filter(t => t.selected);
      }
      
      this.cdr.detectChanges();
    } else {
      // No hay tipos preseleccionados: asegurar que todo esté deseleccionado
      this.constructionTypes.forEach(type => type.selected = false);
      this.selectedTypes = [];
      this.cdr.detectChanges();
    }
  }

  selectType(type: ConstructionType) {
    // En modo calculator, solo permitir una selección a la vez
    if (this.data?.config?.mode === 'calculator') {
      // Si el tipo ya está seleccionado, no hacer nada (mantener selección)
      if (type.selected) {
        this.selectedTypes = type.name === 'Cimentación' ? [] : [type];
        return;
      }
      
      // Deseleccionar todos los tipos primero
      this.constructionTypes.forEach(t => {
        t.selected = false;
      });
      
      // Seleccionar solo el tipo clickeado
      type.selected = true;
      
      // Actualizar array de seleccionados con solo este tipo
      this.selectedTypes = type.name === 'Cimentación' ? [] : [type];
      
      // Force change detection for mobile devices
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // En modo calculator, ejecutar automáticamente la lógica después de la selección
      setTimeout(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        
        // Ejecutar lógica automáticamente
        this.executeCalculatorSelection(type);
      }, 50); // Delay más largo para asegurar que la UI se actualice
      
      return;
    }
    
    // Modo quote: permitir selección múltiple (toggle)
    type.selected = !type.selected;
    this.selectedTypes = this.constructionTypes.filter(t => t.selected);
    this.cdr.markForCheck();
  }

  /**
   * TrackBy function para optimizar ngFor change detection
   */
  trackByTypeId(index: number, type: ConstructionType): string {
    return type.id;
  }

  /**
   * Obtener título dinámico según configuración
   */
  getModalTitle(): string {
    return this.data?.config?.title || '¿Qué vas a construir?';
  }

  /**
   * Obtener subtítulo dinámico según configuración
   */
  getModalSubtitle(): string | undefined {
    return this.data?.config?.subtitle;
  }

  /**
   * Manejador de eventos para ButtonSquareComponent
   * Mantiene la lógica existente de selectType
   */
  onConstructionTypeClick(type: ConstructionType): void {
    this.selectType(type);
  }

  confirm() {
    this.loadingItems = true;
    if (this.selectedTypes.length > 0) {
      // Si es modo calculator, manejar flujo especial
      if (this.data?.config?.mode === 'calculator') {
        // Solo permitir una selección en calculator mode
        const selectedType = this.selectedTypes[0];
        
        // Mapear a WorkTypeDto para el estado
        const workTypeDto: WorkTypeDto = {
          workTypeId: selectedType.id,
          description: selectedType.name,
          sortOrder: 1,
          imgUrl: selectedType.iconUrl
        };
        
        // Guardar en CalculatorStateService
        this.calculatorStateService.setSelectedWorkType(workTypeDto);
        
        // Navegar a calculator para todos los tipos en modo calculator
        this.loadingItems = false;
        this.router.navigate([NAVIGATION_ROUTES.CALCULATOR]);
        this.close();
        return;
      }

      // Flujo normal para quote mode
      this.analytics.trackEvent('prelist_worktype', 'prelist', this.selectedTypes.map(t => t.name).join(', '));
      //this.loadingItems = true;
      this.itemsError = null;
      this.cdr.detectChanges(); // Forzar detección después del cambio inicial

      // Crear array de observables para obtener items de cada tipo seleccionado
      const workItemRequests = this.selectedTypes.map(type =>
        this.workService.getWorkTypeItems(type.id)
      );
      // Ejecutar todas las peticiones en paralelo
      forkJoin(workItemRequests).subscribe({
        next: (itemsArrays: WorkItemDto[][]) => {
          // Combinar todos los items en un solo array
          const allItems = itemsArrays.flat();

          // Filtrar solo items activos y ordenar por sortOrder
          const activeItems = allItems
            .filter(item => item.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder);

          // Guardar los tipos seleccionados en el servicio
          const selectedTypeNames = this.selectedTypes.map(type => type.name);
          const selectedTypeIds = this.selectedTypes.map(type => type.id);
          const selectedTypesData = this.selectedTypes.map(type => ({
            id: type.id,
            name: type.name,
            imgUrl: type.iconUrl
          }));

          this.quoteFlowService.setSelectedConstructionTypes(selectedTypeNames);
          this.quoteFlowService.setSelectedWorkTypeIds(selectedTypeIds);
          this.quoteFlowService.setSelectedWorkTypesData(selectedTypesData);
          this.quoteFlowService.setSelectedTypes(this.selectedTypes);
          this.quoteFlowService.setWorkItems(activeItems);

          // Emitir evento para compatibilidad
          this.confirmed.emit(this.selectedTypes);

          //this.loadingItems = false;
          this.cdr.detectChanges(); // Forzar detección después de completar carga

          // Determinar la ruta de navegación según el modo
          const targetRoute = this.data?.config?.targetRoute || NAVIGATION_ROUTES.QUOTE_PRE_LIST;
          const mode = this.data?.config?.mode || 'quote';
          
          // Si es modo calculator, navegar directamente
          if (mode === 'calculator') {
            this.loadingItems = false;
            this.router.navigate([targetRoute]);
            this.close();
            return;
          }

          // Navegar al pre-list: si hay usuario autenticado, esperar a que createPreList resuelva
          const contractorId = this.authService.currentUser()?.id;          
          if (contractorId) {
            const sections = this.selectedTypes.map(type => ({
              workTypeId: type.id,
              title: type.name
            }));
            
            // Verificar si ya existe un preListId
            const existingPreListId = this.quoteFlowService.getPreListId();
            
            // Preparar la estructura de datos para el servicio
            const createPreListData: any = {
              contractorId,
              sections
            };
            
            // Si existe un preListId, incluirlo en la estructura
            if (existingPreListId) {
              createPreListData.preListId = existingPreListId;
            }
            
            this.workService.createPreList(createPreListData).pipe(
              switchMap(response => {
                // Si el POST no trajo sections, hacer GET para obtenerlas
                if (!response.sections?.length) {
                  return this.workService.getPreList(response.preListId);
                }
                return of(response);
              })
            ).subscribe({
              next: (response) => {            
                this.quoteFlowService.setPreListId(response.preListId);
                // Limpiar datos de un flujo anterior
                this.quoteFlowService.setPreListData({ selectedConstructionTypes: [], items: [] });
                if (response.sections?.length) {
                  const sectionMap: Record<string, string> = {};
                  response.sections.forEach((s: any) => {
                    const sectionId = s.preListSectionId || s.sectionId || s.id;
                    const wTypeId = s.workTypeId || s.work_type_id;
                    if (sectionId && wTypeId) sectionMap[wTypeId] = sectionId;
                  });
                  this.quoteFlowService.setPreListSections(sectionMap);
                }
                this.loadingItems = false;    
                this.router.navigate([NAVIGATION_ROUTES.QUOTE_PRE_LIST]);
                this.close();
              },
              error: (err) => {
                console.error('Error creating pre-list:', err);
                 this.loadingItems = false;    
                this.router.navigate([NAVIGATION_ROUTES.QUOTE_PRE_LIST]);
                this.close();
              }
            });
          } else {
            // Sin sesión, generar un GUID aleatorio y crear pre-lista
            const randomContractorId = this.generateGuid();
            const sections = this.selectedTypes.map(type => ({
              workTypeId: type.id,
              title: type.name
            }));
            
            // Verificar si ya existe un preListId
            const existingPreListId = this.quoteFlowService.getPreListId();
            
            // Preparar la estructura de datos para el servicio
            const createPreListData: any = {
              contractorId: randomContractorId,
              sections
            };
            
            // Si existe un preListId, incluirlo en la estructura
            if (existingPreListId) {
              createPreListData.preListId = existingPreListId;
            }
            
            this.workService.createPreList(createPreListData).pipe(
              switchMap(response => {
                // Si el POST no trajo sections, hacer GET para obtenerlas
                if (!response.sections?.length) {
                  return this.workService.getPreList(response.preListId);
                }
                return of(response);
              })
            ).subscribe({
              next: (response) => {            
                this.quoteFlowService.setPreListId(response.preListId);
                // Limpiar datos de un flujo anterior
                this.quoteFlowService.setPreListData({ selectedConstructionTypes: [], items: [] });
                if (response.sections?.length) {
                  const sectionMap: Record<string, string> = {};
                  response.sections.forEach((s: any) => {
                    const sectionId = s.preListSectionId || s.sectionId || s.id;
                    const wTypeId = s.workTypeId || s.work_type_id;
                    if (sectionId && wTypeId) sectionMap[wTypeId] = sectionId;
                  });
                  this.quoteFlowService.setPreListSections(sectionMap);
                }
                this.loadingItems = false;    
                this.router.navigate([NAVIGATION_ROUTES.QUOTE_PRE_LIST]);
                this.close();
              },
              error: (err) => {
                console.error('Error creating pre-list:', err);
                 this.loadingItems = false;    
                this.router.navigate([NAVIGATION_ROUTES.QUOTE_PRE_LIST]);
                this.close();
              }
            });
          }
        },
        error: (error) => {
          console.error('Error loading work items:', error);
          this.itemsError = 'Error al cargar los productos';
          this.loadingItems = false;
          this.cdr.detectChanges(); // Forzar detección después de error
        }
      });
    }
  }

  close() {
    this.bottomSheetRef.dismiss(this.selectedTypes.length > 0 ? this.selectedTypes : undefined);
    this.resetSelection();
  }

  private resetSelection() {
    this.constructionTypes.forEach(type => type.selected = false);
    this.selectedTypes = [];
  }

  /**
   * Genera un GUID aleatorio
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  get hasSelection(): boolean {
    return this.selectedTypes.length > 0;
  }

  get selectionCount(): number {
    return this.selectedTypes.length;
  }

  /**
   * Calcula el height dinámico del modal basado en el modo y cantidad de constructionTypes
   */
  get modalHeight(): string {
    const itemCount = this.constructionTypes.length;
    
    if (itemCount === 0) {
      return 'h-[262px]'; // Default para loading/error states
    }
    
    // En modo calculator: altura fija mínima de 262px
    if (this.isCalculatorMode) {
      // Calcular número de filas (2 columnas por fila)
      const rows = Math.ceil(itemCount / 2);
      
      // Alturas para calculator (sin botón al bottom):
      // Header: ~88px
      // Padding interno: ~32px  
      // Cada fila: ~146px (142px item + 4px gap)
      
      const baseHeight = 88 + 32; // 120px base (sin footer)
      const contentHeight = rows * 146;
      const totalHeight = baseHeight + contentHeight;
      
      // Mínimo 262px para calculator
      const finalHeight = Math.max(totalHeight, 262);
      
      return `h-[${finalHeight}px]`;
    }
    
    // En modo quote: usar max-height 90vh con scroll
    return 'max-h-[90vh]';
  }

  /**
   * Determina si necesita scroll basado en el contenido y modo
   */
  get needsScroll(): boolean {
    const itemCount = this.constructionTypes.length;
    
    // En modo calculator: nunca necesita scroll (altura dinámica)
    if (this.isCalculatorMode) {
      return false;
    }
    
    // En modo quote: siempre permitir scroll para asegurar que todas las opciones sean visibles
    // El scroll se maneja mediante CSS y altura máxima
    return itemCount > 0;
  }

  /**
   * Determina si el modal está en modo calculator (selección única)
   */
  get isCalculatorMode(): boolean {
    return this.data?.config?.mode === 'calculator';
  }

  /**
   * Ejecuta la lógica de selección automática para el modo calculator
   */
  private executeCalculatorSelection(type: ConstructionType): void {
    // Para todos los tipos de trabajo en modo calculator, ejecutar la lógica de confirmación
    this.executeCalculatorConfirm(type);
  }

  /**
   * Ejecuta la lógica de confirmación para calculator mode
   */
  private executeCalculatorConfirm(selectedType: ConstructionType): void {
    this.loadingItems = true;
    this.analytics.trackEvent('calculator_flow', 'calculator', selectedType.name);

    // Mapear a WorkTypeDto para el estado
    const workTypeDto: WorkTypeDto = {
      workTypeId: selectedType.id,
      description: selectedType.name,
      sortOrder: 1,
      imgUrl: selectedType.iconUrl
    };
    
    // Guardar en CalculatorStateService
    this.calculatorStateService.setSelectedWorkType(workTypeDto);
    
    // Navegar a calculator
    setTimeout(() => {
      this.loadingItems = false;
      this.router.navigate([NAVIGATION_ROUTES.CALCULATOR]);
      this.close();
    }, 300); // Pequeño delay para mostrar feedback visual
  }
}
